import React, { useContext } from "react";
import { Box, Flex, Text, Button, Link } from "@chakra-ui/react";
import { GiWaterDrop } from "react-icons/gi";
import { IoIosPulse } from "react-icons/io";
import { FaFire } from "react-icons/fa";
import { RiAlertFill } from "react-icons/ri";
import { useSelector } from "react-redux";
import { LinkTo } from "../../../components/nav/components/LinkTo";
import { NavLink } from "react-router-dom";
import { UserProfileContext } from "../../../context/UserContext";
import { resolveServerRate, blockingReasonMessages } from "../../../utils/residentPaymentSettings";
import { estateCommodityUnit } from "../../../utils/commodityUnits";

const splitNairaKobo = (value) => {
  const num = parseFloat(value);
  if (!Number.isFinite(num) || num <= 0) return null;
  const [naira, kobo = "00"] = num.toFixed(2).split(".");
  return {
    naira: Number(naira).toLocaleString("en-NG"),
    kobo: kobo.padEnd(2, "0"),
  };
};

export const RateCard = ({
  rateData,
  pageColor: pageColorProp,
  utilityType: utilityTypeProp,
}) => {
  const fallbackDisplayData = useSelector(
    (state) => state?.switchTab?.currentDisplay?.dashboard?.rate,
  );
  const displayData = rateData || fallbackDisplayData || {};
  const { activeAccount } = useContext(UserProfileContext) || {};
  const fallbackPageColor = useSelector(
    (state) => state?.switchTab?.brandColor?.brandColor,
  );
  const pageColor = pageColorProp || fallbackPageColor;

  const fallbackUtilityType = useSelector(
    (state) => state?.switchTab?.currentDisplay?.utilityType,
  );
  const utilityType = utilityTypeProp || fallbackUtilityType;

  const gas = utilityType === "gas";
  const water = utilityType === "water";
  const electricity = utilityType === "electricity";

  // The estate payload (GET /users/estates) is authoritative for rate and
  // unit. Missing/0 rate is "Rate unavailable" — never a fallback number.
  const serverRate = resolveServerRate(activeAccount, utilityType);
  const rate = serverRate.authoritative ? serverRate.rate : displayData.rate;
  const unitText = estateCommodityUnit(activeAccount?.commodityUnits, utilityType);
  const unit = unitText ? `/${unitText}` : "";
  const parsed = splitNairaKobo(rate);
  const unavailableReason = blockingReasonMessages(serverRate.reasons)[0];
  return (
    <Box
      bg="#fff"
      borderRadius="10px"
      p="20px"
      height={"200px"}
      className="box-info"
      shadow={"md"}
    >
      <Flex>
        <Box>
          <Text fontSize="20px" fontWeight="bold" color="primary" mb="5px">
            Rate
          </Text>
        </Box>
      </Flex>

      <Flex justifyContent="space-between">
        <Box>
          {parsed ? (
            <Flex alignItems="baseline" mt="10px" gap="1px">
              {/* ₦ symbol */}
              <Text fontSize="18px" fontWeight="bold" color="gray.700" lineHeight="1">
                ₦
              </Text>
              {/* Naira — large */}
              <Text fontSize="26px" fontWeight="bold" color="gray.800" lineHeight="1">
                {parsed.naira}
              </Text>
              {/* .kobo — smaller, muted */}
              <Text fontSize="14px" fontWeight="600" color="gray.400" lineHeight="1">
                .{parsed.kobo}
              </Text>
              {/* unit label */}
              {unit && (
                <Text fontSize="13px" color="gray.400" ml="4px" lineHeight="1">
                  {unit}
                </Text>
              )}
            </Flex>
          ) : (
            <Box mt="10px">
              <Text fontSize="14px" fontWeight="semibold" color="gray.500">
                Rate unavailable
              </Text>
              {unavailableReason && (
                <Text fontSize="12px" color="gray.400">{unavailableReason}</Text>
              )}
            </Box>
          )}
        </Box>
      </Flex>

      <Flex mt="15px" justifyContent="space-between">
        <Box>
          {water ? (
            <GiWaterDrop color={pageColor} size={30} />
          ) : gas ? (
            <FaFire color={pageColor} size={30} />
          ) : electricity ? (
            <IoIosPulse color={pageColor} size={30} />
          ) : (
            ""
          )}
        </Box>
      </Flex>
    </Box>
  );
};
